import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, PlusCircle, FolderOpen, FileText, Settings,
  ChevronLeft, ChevronRight, LogOut, Search, User, Users
} from "lucide-react";

export default function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isTeam = !!user?.team_id;
  const isTeamAdmin = user?.team_role === "owner" || user?.team_role === "admin";

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/analyze/new", icon: PlusCircle, label: "New Analysis", hide: user?.team_role === "viewer" },
    { to: "/analyses", icon: FolderOpen, label: "My Analyses" },
    ...(isTeamAdmin ? [{ to: "/team", icon: Users, label: "Team" }] : []),
    { to: "/reports", icon: FileText, label: "Reports" },
    { to: "/settings", icon: Settings, label: "Settings" },
  ].filter(i => !i.hide);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FC] flex">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`fixed top-0 left-0 h-full z-40 flex flex-col transition-all duration-300 ${
          collapsed ? "w-16" : "w-56"
        }`}
        style={{ background: "#0F172A" }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-white/10">
          {!collapsed && (
            <img src="/adbit-logo.png" alt="AdBit" className="h-7 w-auto brightness-0 invert" />
          )}
          {collapsed && (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-fuchsia-500 flex items-center justify-center mx-auto">
              <span className="text-white text-xs font-bold">A</span>
            </div>
          )}
        </div>
        {isTeam && !collapsed && (
          <div className="px-4 py-2 border-b border-white/10">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Team</p>
            <p className="text-xs font-medium text-slate-300 truncate">{user?.team_name}</p>
          </div>
        )}

        {/* Nav items */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={(e) => item.disabled && e.preventDefault()}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  item.disabled
                    ? "text-slate-600 cursor-not-allowed"
                    : isActive
                    ? "bg-orange-500/20 text-orange-400"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                } ${collapsed ? "justify-center" : ""}`
              }
            >
              <item.icon size={18} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          data-testid="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          className="mx-2 mb-2 p-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-center"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>

        {/* User section */}
        <div className="border-t border-white/10 p-3">
          <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
            <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <User size={14} className="text-orange-400" />
              )}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">{user?.name || "User"}</p>
                <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
              </div>
            )}
            {!collapsed && (
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-white/5 transition-colors"
              >
                <LogOut size={14} />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? "ml-16" : "ml-56"}`}>
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3 flex-1 max-w-md">
            <Search size={16} className="text-slate-400" />
            <input
              data-testid="search-input"
              type="text"
              placeholder="Search analyses..."
              className="text-sm text-slate-700 bg-transparent outline-none flex-1 placeholder:text-slate-400"
              onChange={(e) => {
                const q = e.target.value.toLowerCase();
                if (q.length > 0) navigate(`/analyses?q=${encodeURIComponent(q)}`);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  navigate(`/analyses?q=${encodeURIComponent(e.target.value.trim())}`);
                }
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/settings")} className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center hover:ring-2 hover:ring-orange-200 transition-all cursor-pointer">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <span className="text-xs font-bold text-orange-600">{user?.name?.[0] || "U"}</span>
              )}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
}
